import _ from "lodash";
import defaultSummaryConfig from "../models/summary/defaultConfig.json";
import client, { configuration } from "../api/client/SdsWebApiClient";
import { SummaryConfig, Convert } from "../models/summary/Config";
import { SdsAccountInfo } from "../api/generated";
import { getRouterBasename } from "./navigationUtils";

// defaultAppConfig.json is served as a static public asset (so the mobile
// remote-control page can also fetch it for its translations). Resolve it
// relative to the SPA's origin — NOT `configuration.basePath`, which can
// point at a separately-hosted API (e.g. via the `service_url` query param)
// that doesn't serve the SPA's static assets. Cache at module scope so
// multiple callers share one network hit.
let defaultAppConfigPromise: Promise<any> | null = null;
export const getDefaultAppConfigOnce = (): Promise<any> => {
    if (!defaultAppConfigPromise) {
        const spaRoot = getRouterBasename(window.location.pathname);
        const url = `${window.location.origin}${spaRoot}defaultAppConfig.json`;
        defaultAppConfigPromise = fetch(url, { cache: "no-cache" })
            .then(r => {
                if (!r.ok) throw new Error(`defaultAppConfig.json fetch failed (${r.status})`);
                return r.json();
            })
            .catch(err => {
                // Reset the cache so the next caller retries instead of permanently failing.
                defaultAppConfigPromise = null;
                throw err;
            });
    }
    return defaultAppConfigPromise;
};

const getAppConfig = async (path: string | undefined | null = null): Promise<any> => {
    const defaults = await getDefaultAppConfigOnce();
    if (!path) return defaults;
    const response = await fetch(path, { cache: "no-cache" });
    const config = await response.json();
    return _.merge(defaults, config);
};

const getSummaryData = async (path: string | undefined | null = null): Promise<SummaryConfig> => {
    if (!path) return Convert.toSummaryConfig(JSON.stringify(defaultSummaryConfig));
    const response = await fetch(path, { cache: "no-cache" });
    const config = await response.json();

    return Convert.toSummaryConfig(JSON.stringify(config || defaultSummaryConfig));
};

export const loadConfigurationFiles = async (
    language: string,
    account?: string | null
): Promise<[Object, SummaryConfig]> => {
    const configurationFilesUrls = await client.getConfigurationFilesGet(language, account || undefined);
    return Promise.all([
        getAppConfig(configurationFilesUrls.stylingFile),
        getSummaryData(configurationFilesUrls.summaryFile),
    ]);
};

export const getFrontendSettings = async () => {
    const response = await fetch(`${configuration.basePath}/frontend_settings.json`, { cache: "no-cache" });
    const settings = await response.json();
    return settings;
};

export const getAppVersion = async () => {
    const response = await fetch("./appversion.txt", { cache: "no-cache" });
    if (response.ok) {
        const appVersion = (await response.text()).trim();
        if (/^\d+(\.\d+)*$/.test(appVersion)) {
            return appVersion;
        }
    }
    return "Development";
};

export const loadAppCustomization = async (account: SdsAccountInfo) => {
    const response = await fetch(`${account.publicContentDir}/customization.json`, { cache: "no-cache" });
    return response.json();
};

export const getModelFromConfig = (config: string) => {
    // Assuming the config is always in either classic or swift "default" encoding,
    // with the model at the start of the string.
    const firstDot = config.indexOf(".");
    const firstSemicolon = config.indexOf(";");
    let modelEndIndex;
    if (firstDot !== -1 && firstSemicolon !== -1) {
        modelEndIndex = Math.min(firstDot, firstSemicolon);
    } else if (firstDot !== -1) {
        modelEndIndex = firstDot;
    } else {
        modelEndIndex = firstSemicolon;
    }
    return modelEndIndex === -1 ? config : config.substring(0, modelEndIndex);
};
